// Reminders domain — shared, dependency-free bits for the mobile screens
// (query keys, type palette, pure date/label helpers). Ported from the web
// implementations (client/src/site/components/reminders/reminder-shared.tsx,
// contacts/contact-data.ts, reminders/calendar-month-grid.tsx); like
// catalog-data.ts's CATALOG_ACCENTS, the web keeps its own date-fns copies —
// this module stays dependency-free so mobile's standalone node_modules needs
// nothing new. The Eden fetchers live in mobile/src/api/reminders.ts, where
// the treaty client's inferred response types survive.

export const REMINDER_TYPE_CODES = ["birthday", "anniversary", "custom", "managed"] as const;
export type ReminderTypeCode = (typeof REMINDER_TYPE_CODES)[number];

/** Where SEND A CARD lands (web `browsePathFor`): the recipient's browse page for the typed
 *  reminders; `custom` keeps the catalog, as legacy had no browse page for it either. */
export function hasBrowsePage(type: ReminderTypeCode): boolean {
  return type !== "custom";
}

/** Row shape of GET /user/reminders (list projection incl. joined contact/occasion). */
export interface ReminderRow {
  reminder_id: string;
  reminder_type_code: ReminderTypeCode;
  contact_id: string | null;
  occasion_id: string | null;
  label: string | null;
  event_month: number | null;
  event_day: number | null;
  event_year: number | null;
  recurrence_code: "yearly" | "once" | "monthly";
  next_occurrence_on: string | null;
  is_email_enabled: boolean;
  is_push_enabled: boolean;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  /** The member's OWN birthday, on the `is_self` contact signup provisions (PRD 02 D16).
   *  Shown as "Your Birthday" with an Edit action (displayNameOf) — "Send a Card Now"
   *  would offer to send the member a card addressed to themselves. Null for a reminder
   *  with no contact (the join is a LEFT JOIN). Optional: GET /reminders and
   *  /reminders/upcoming select it (BDAY-270); other endpoints' rows leave it absent. */
  is_self?: boolean | null;
  occasion_name: string | null;
  /** "DETAILS" (legacy calendar.notes_tx, added 2026-10-22). GET /reminders + /reminders/upcoming
   *  select it; absent (undefined) on other endpoints' rows — never write it back from one. */
  notes?: string | null;
  /** The card made for the upcoming occurrence (GET /reminders + /calendar + /upcoming);
   *  absent on rows from other endpoints. Legacy My Reminders: Card Scheduled / Sent /
   *  Continue Card + "send another card". */
  card_send_id?: string | null;
  card_status?: "draft" | "scheduled" | "sent" | null;
  /** Managed (holiday) rows from GET /reminders: every generated upcoming date
   *  (`YYYY-MM-DD`, ~3 years) — My Calendar places moving holidays past the next one. */
  occurrence_dates?: string[];
}

/** Legacy reminder-card icon-column gradients (reminder-card/styles.scss type_1..4),
 *  top → bottom. Web builds a CSS gradient, the app an expo-linear-gradient. */
export const REMINDER_CARD_GRADIENT: Record<ReminderTypeCode, readonly [string, string]> = {
  birthday: ["#f7b944", "#f1b84d"],
  anniversary: ["#e83073", "#ea4682"],
  managed: ["#f05220", "#dd5b34"],
  custom: ["#0397ac", "#17bacf"],
};

/** The scattered white dots on that column (legacy reminder-card-dots, viewBox 0 0 91 220):
 *  [cx, cy, opacity?]. */
export const REMINDER_CARD_DOTS: readonly (readonly [number, number, number?])[] = [
  [17.2, 39.7],
  [44, 194.4, 0.58],
  [20.5, 170.9],
  [89.1, 152.1, 0.6],
  [81.6, 39.3],
  [57.8, 69.3],
  [86.3, 86.3],
  [1.6, 86.3],
  [25.2, 1.6, 0.4],
  [1.6, 138, 0.4],
  [30.8, 59.8, 0.7],
  [20.5, 217.9, 0.5],
  [59.7, 167.6],
  [86.3, 194.4],
  [86.3, 6.3, 0.7],
  [57.6, 6.3],
  [48.2, 34.6, 0.5],
];

/** Row of GET /user/reminders/upcoming — a list row + the card already made for the
 *  upcoming occurrence (the /home "Upcoming Reminders" strip). */
export interface UpcomingReminderRow extends ReminderRow {
  card_send_id: string | null;
  /** null → no card yet ("Send a card now"); draft → "Continue Card"; else done. */
  card_status: "draft" | "scheduled" | "sent" | null;
  /** The member's OWN birthday, on the `is_self` contact signup provisions (PRD 02 D16).
   *  The strip shows it as "Your Birthday" with an Edit action — "Send a Card Now" would
   *  offer to send the member a card addressed to themselves. Null for a reminder with no
   *  contact (the join is a LEFT JOIN). */
  is_self: boolean | null;
}

export const reminderKeys = {
  list: (type?: string | null) => ["/api/user/reminders", "list", type ?? null] as const,
  /** GET /reminders/upcoming — the /home + app home "Upcoming Reminders" strip. */
  upcoming: ["/api/user/reminders", "upcoming"] as const,
  contacts: (group?: string | null) => ["/api/user/contacts", group ?? null] as const,
  groups: ["/api/user/groups"] as const,
};

/** Requester-side Birthday Request caches — one definition so the same mutations
 *  invalidate the same things on both platforms. */
export const brSenderKeys = {
  list: ["/api/user/birthday-requests"] as const,
  generalLink: ["/api/user/me/birthday-request-link"] as const,
};

export type RemindersSort = "next" | "first" | "last";

/** Web reminders toolbar "SORT BY" options (old order_by: next_date/first/last). */
export const SORT_ITEMS: { value: RemindersSort; label: string }[] = [
  { value: "next", label: "Next Date" },
  { value: "first", label: "First Name" },
  { value: "last", label: "Last Name" },
];

/** Contact groups are HIDDEN on mobile until the manage-groups surface lands
 *  (PRD 02 D14 — web has it, mobile can't create groups yet). Flip to `true` to
 *  restore the group chips (contact rows + add/edit forms) everywhere at once. */
export const GROUPS_ENABLED: boolean = false;

export type ContactsSort = "first" | "last";

/** Web contacts (/address_book) "SORT BY" options; default is last name. */
export const CONTACT_SORT_ITEMS: { value: ContactsSort; label: string }[] = [
  { value: "first", label: "First Name" },
  { value: "last", label: "Last Name" },
];

/** Web reminders toolbar "SHOWING" options — "all" plus each type code. */
export const TYPE_ITEMS: { value: "all" | ReminderTypeCode; label: string }[] = [
  { value: "all", label: "All Reminders" },
  { value: "birthday", label: "Birthday Reminders" },
  { value: "anniversary", label: "Anniversary Reminders" },
  { value: "managed", label: "Holiday Reminders" },
  { value: "custom", label: "Event Reminders" },
];

export const TYPE_LABEL: Record<ReminderTypeCode, string> = {
  birthday: "Birthday",
  anniversary: "Anniversary",
  managed: "Holiday",
  custom: "Event",
};

/** Old type-colour mapping: calendarTypeId 1/2/3/4 → hex (menu circles + row squares). */
export const TYPE_COLOR: Record<ReminderTypeCode, string> = {
  birthday: "#f7b944",
  anniversary: "#e83073",
  managed: "#f05320",
  custom: "#0397ac",
};

/** Calendar-grid chip icon colours — the web grid's text-primary /
 *  text-cal-anniversary / text-cal-holiday / text-secondary-dark, resolved. */
export const CAL_ICON_COLOR: Record<ReminderTypeCode, string> = {
  birthday: "#17a17e",
  anniversary: "#fa2630",
  managed: "#eeaa83",
  custom: "#3b87ab",
};

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** 1 → "1st", 22 → "22nd", 13 → "13th" — date-fns "do" for day-of-month. */
function ordinalOf(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** [year, month, day] of a "yyyy-mm-dd" date string (no Date/timezone involved). */
function partsOf(ymd: string): [number, number, number] {
  const [y = 0, m = 0, d = 0] = ymd.split("-").map(Number);
  return [y, m, d];
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "2026-10-04" → "Oct 04" (web `format(d, "MMM dd")`), no Date/timezone round-trip. */
export function shortDate(iso: string | null): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return m ? `${SHORT_MONTHS[Number(m[2]) - 1] ?? ""} ${m[3]}` : "";
}

/** The row's name everywhere it's shown, searched or exported. The member's own
 *  self-contact row reads "Your Birthday" / "Your Anniversary" (BDAY-270). */
export function displayNameOf(r: ReminderRow): string {
  if (r.is_self === true) return `Your ${TYPE_LABEL[r.reminder_type_code]}`;
  const fullName = [r.first_name, r.last_name].filter(Boolean).join(" ");
  return r.label || fullName || r.occasion_name || "Reminder";
}

/** What both clients name the exported file. */
export const REMINDERS_CSV_FILENAME = "reminders.csv";

/** One CSV field: quoted (RFC 4180) only when it holds a comma, a quote or a line break. */
function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/**
 * The date a member keeps for the reminder, as a spreadsheet reads it: `M/D/YYYY`, or `M/D`
 * when the year is unknown. Legacy exported the stored event date (the birth date with its
 * year), never the rolled-forward next occurrence — which is only right for a holiday,
 * whose date lives in the occasion rather than on the reminder.
 */
export function exportDateOf(r: ReminderRow): string {
  if (r.event_month) {
    // A month with no day (the API accepts them independently) still exports what is known.
    return [r.event_month, r.event_day, r.event_year].filter(Boolean).join("/");
  }
  if (!r.next_occurrence_on) return "";
  const [y, m, d] = partsOf(r.next_occurrence_on);
  return `${m}/${d}/${y}`;
}

/**
 * The reminders export, `TYPE,NAME,DATE,NOTES,EMAIL` — legacy's `onExport` columns plus the
 * reminder's notes and the contact's email, which members asked for. New columns go on the
 * end so a spreadsheet built on the earlier shape keeps reading. Shared so the site and the
 * app emit byte-identical files instead of two implementations drifting apart.
 *
 * Pass the rows the member is actually LOOKING at: legacy re-fetched the whole list and
 * ignored the filters, which meant the file never matched the screen. Both clients hand
 * over their filtered, sorted list instead.
 */
export function remindersCsv(reminders: ReminderRow[]): string {
  const lines = ["TYPE,NAME,DATE,NOTES,EMAIL"];
  for (const r of reminders) {
    lines.push(
      [
        TYPE_LABEL[r.reminder_type_code],
        csvField(displayNameOf(r)),
        exportDateOf(r),
        csvField(r.notes ?? ""),
        // Blank for a holiday or a contact without one, never "null".
        csvField(r.email ?? ""),
      ].join(","),
    );
  }
  return lines.join("\n");
}

/** Old app's `getPrettyDate` shape: "January 1st, 1990", year omitted when unknown. */
export function formatEventDate(reminder: {
  event_month: number;
  event_day: number;
  event_year: number | null;
}): string {
  const base = `${MONTH_NAMES[reminder.event_month - 1]} ${ordinalOf(reminder.event_day)}`;
  return reminder.event_year ? `${base}, ${reminder.event_year}` : base;
}

/** Old `getReminderDisplayDate`: pretty event date plus an age decorator —
 *  "(age N)" for birthdays, "(N years)" for anniversaries/yearly events. */
export function displayDateOf(r: ReminderRow): string {
  let pretty = "";
  if (r.event_month && r.event_day) {
    pretty = formatEventDate({
      event_month: r.event_month,
      event_day: r.event_day,
      event_year: r.event_year,
    });
  } else if (r.next_occurrence_on) {
    const [y, m, d] = partsOf(r.next_occurrence_on);
    pretty = `${MONTH_NAMES[m - 1]} ${ordinalOf(d)}, ${y}`;
  }
  if (r.event_year && r.next_occurrence_on) {
    const age = partsOf(r.next_occurrence_on)[0] - r.event_year;
    if (age > 0) {
      if (r.reminder_type_code === "birthday") pretty += ` (age ${age})`;
      else if (r.reminder_type_code === "anniversary" || r.recurrence_code === "yearly") {
        pretty += ` (${age} years)`;
      }
    }
  }
  return pretty;
}

/** "July 2026" month bucket of a row's next occurrence (drives the dividers). */
export function monthHeaderOf(r: ReminderRow): string | null {
  if (!r.next_occurrence_on) return null;
  const [y, m] = partsOf(r.next_occurrence_on);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

/** Day-of-month for the row's big serif number. */
export function dayNumberOf(r: ReminderRow): string {
  if (r.next_occurrence_on) return String(partsOf(r.next_occurrence_on)[2]);
  return r.event_day != null ? String(r.event_day) : "";
}

/** The reminder form's standing hint under the frequency control, also the error when a
 *  "Once Only" date is in the past (client/src/site/components/reminders/reminder-dialogs.tsx
 *  renders it prefixed with "* "). */
export const PAST_DATE_HINT = "For past events, set frequency to 'Every Year'";

/** Is this calendar date before the server's "today"? The server rolls dates forward
 *  against midnight UTC (server/lib/reminder-dates.ts `todayUtc`), so a local-midnight
 *  check would let a member west of UTC save a once-only date the server stores as null. */
export function isBeforeServerToday(year: number, month: number, day: number): boolean {
  const now = new Date();
  return (
    Date.UTC(year, month - 1, day) <
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
}

/** Days in month `m` (1-12) of `y` — mirrors the server's daysInMonth. */
function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

/** The concrete dates of a non-yearly row: a holiday's generated occurrences (else its
 *  next one), or a one-off's single date. */
export function concreteDates(r: ReminderRow): string[] {
  if (r.reminder_type_code === "managed" && r.occurrence_dates?.length) return r.occurrence_dates;
  return r.next_occurrence_on ? [r.next_occurrence_on] : [];
}

/** Does reminder `r` land on calendar `day`? Managed + once-only match the concrete
 *  next_occurrence date; yearly personal reminders match month/day in any year, and
 *  monthly ones the day in every month. Overshoots clamp to the last day of the month
 *  — so Feb 29 shows on Feb 28 in common years, exactly where the server materialises
 *  it (server/lib/reminder-dates.ts `occurrenceInYear`). */
export function occursOnDay(r: ReminderRow, day: Date): boolean {
  if (r.reminder_type_code === "managed" || r.recurrence_code === "once") {
    return concreteDates(r).some((iso) => {
      const [y, m, d] = partsOf(iso);
      return y === day.getFullYear() && m === day.getMonth() + 1 && d === day.getDate();
    });
  }
  let month = r.event_month;
  let date = r.event_day;
  if (month == null || date == null) {
    if (!r.next_occurrence_on) return false;
    const [, mm, dd] = partsOf(r.next_occurrence_on);
    if (!Number.isFinite(mm) || !Number.isFinite(dd)) return false;
    month = mm;
    date = dd;
  }
  const cellMonth = day.getMonth() + 1;
  const cellDate = day.getDate();
  // Monthly (legacy rows; not offered in the new UI) recurs on event_day every month.
  if (r.recurrence_code !== "monthly" && month !== cellMonth) return false;
  return cellDate === Math.min(date, daysInMonth(day.getFullYear(), cellMonth));
}

/** Does reminder `r` fall in the month of `month`? Managed/once match the concrete
 *  year+month; yearly personal reminders match the month in any year, and monthly
 *  ones every month. */
export function occursInMonth(r: ReminderRow, month: Date): boolean {
  const year = month.getFullYear();
  const monthNum = month.getMonth() + 1;
  if (r.reminder_type_code === "managed" || r.recurrence_code === "once") {
    return concreteDates(r).some((iso) => {
      const [yy, mm] = partsOf(iso);
      return yy === year && mm === monthNum;
    });
  }
  if (r.recurrence_code === "monthly") return true;
  let mo = r.event_month;
  if (mo == null && r.next_occurrence_on) mo = partsOf(r.next_occurrence_on)[1];
  return mo === monthNum;
}

/** Black/white text that stays legible on a solid `hex` fill (YIQ luminance,
 *  threshold 145) — the web's groupChipStyle helper. */
export function readableTextColor(hex: string): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return "#1a1a1a";
  const int = Number.parseInt(match[1]!, 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  return (r * 299 + g * 587 + b * 114) / 1000 >= 145 ? "#1a1a1a" : "#ffffff";
}

/** Grid label: person name (+ age in parens) for birthday/anniversary, else the
 *  event/holiday name — the old `getReminderDisplayName`. */
export function gridNameOf(r: ReminderRow, displayedYear: number): string {
  if (r.reminder_type_code === "birthday" || r.reminder_type_code === "anniversary") {
    const base = [r.first_name, r.last_name].filter(Boolean).join(" ") || r.label || "Reminder";
    const age = r.event_year ? displayedYear - r.event_year : 0;
    return age > 0 ? `${base} (${age})` : base;
  }
  return r.label || r.occasion_name || "Reminder";
}

// ── Occasions (holiday browse) ────────────────────────────────────────────────
// Ports the web holidays.tsx browse tree + date helpers (see
// client/src/site/pages/holidays.tsx). Dependency-free — no date-fns.

/** Row of GET /user/occasions — an occasion with the member's subscription state. */
export interface Occasion {
  occasion_id: string;
  occasion_key: string;
  occasion_name: string;
  variable_type: string | null;
  is_global: boolean;
  /** Fixed-date rule (null for variable holidays like Easter). */
  month: number | null;
  day: number | null;
  subscribed: boolean;
  blocked: boolean;
  next_occurrence_on: string | null;
}

/** Category-group row of GET /user/occasions (legacy holiday_group hierarchy). */
export interface OccasionGroup {
  occasion_group_id: string;
  parent_occasion_group_id: string | null;
  group_key: string;
  name: string;
  position: number;
  occasion_ids: string[];
}

export interface OccasionsResponse {
  occasions: Occasion[];
  groups: OccasionGroup[];
}

/** A subgroup accordion within a tab (e.g. "Christian" under Religious). */
export interface HolidaySubgroup {
  group: OccasionGroup;
  occasions: Occasion[];
}

/** A browse tab (root child): its own occasions plus subgroup accordions. */
export interface HolidayTab extends HolidaySubgroup {
  subgroups: HolidaySubgroup[];
}

/** Next occurrence "yyyy-mm-dd" — the generated row, else derived from a fixed
 *  month/day rule (today or later this year, otherwise next year). Variable
 *  holidays without an occurrence row have no date. */
export function nextOccasionDate(o: Occasion): string | null {
  if (o.next_occurrence_on) return o.next_occurrence_on;
  if (!o.month || !o.day) return null;
  const now = new Date();
  const passed =
    o.month < now.getMonth() + 1 || (o.month === now.getMonth() + 1 && o.day < now.getDate());
  const year = passed ? now.getFullYear() + 1 : now.getFullYear();
  return `${year}-${String(o.month).padStart(2, "0")}-${String(o.day).padStart(2, "0")}`;
}

/** Old `getPrettyDate`: "January 1st, 2027" (empty when no date is known). */
export function prettyOccasionDate(o: Occasion): string {
  const date = nextOccasionDate(o);
  if (!date) return "";
  const [y, m, d] = partsOf(date);
  return `${MONTH_NAMES[m - 1]} ${ordinalOf(d)}, ${y}`;
}

/** Old lists run in calendar order (month/day of the next occurrence), dateless last. */
export function byOccasionCalendarOrder(a: Occasion, b: Occasion): number {
  const keyOf = (o: Occasion) => {
    const date = nextOccasionDate(o);
    return date
      ? Number(date.slice(5, 7)) * 100 + Number(date.slice(8, 10))
      : Number.MAX_SAFE_INTEGER;
  };
  return keyOf(a) - keyOf(b) || a.occasion_name.localeCompare(b.occasion_name);
}

/** Old tab order was Global / Religious / National regardless of stored position. */
const OCCASION_TAB_ORDER = ["Global", "Religious", "National"];
function byOccasionTabOrder(a: OccasionGroup, b: OccasionGroup): number {
  const rank = (g: OccasionGroup) => {
    const index = OCCASION_TAB_ORDER.findIndex((prefix) => g.name.startsWith(prefix));
    return index === -1 ? OCCASION_TAB_ORDER.length : index;
  };
  return rank(a) - rank(b) || a.position - b.position;
}

/** Assemble the browse tree: root → tabs → subgroup accordions (web `buildTabs`). */
export function buildHolidayTabs(data: OccasionsResponse | undefined): HolidayTab[] {
  if (!data) return [];
  const occasionById = new Map(data.occasions.map((o) => [o.occasion_id, o]));
  const resolve = (group: OccasionGroup) =>
    group.occasion_ids
      .map((id) => occasionById.get(id))
      .filter((o): o is Occasion => !!o)
      .sort(byOccasionCalendarOrder);

  const root = data.groups.find((g) => !g.parent_occasion_group_id);
  if (!root) return [];
  return data.groups
    .filter((g) => g.parent_occasion_group_id === root.occasion_group_id)
    .sort(byOccasionTabOrder)
    .map((group) => ({
      group,
      occasions: resolve(group),
      subgroups: data.groups
        .filter((g) => g.parent_occasion_group_id === group.occasion_group_id)
        .sort((a, b) => a.position - b.position)
        .map((sub) => ({ group: sub, occasions: resolve(sub) }))
        .filter((sub) => sub.occasions.length > 0),
    }));
}

// ── Reminder Share shapes (PRD 02 D13) — the /user/shares + /shares/:token
//    endpoints are typed t.Any() server-side, so the row shapes live here,
//    shared by the web dialogs and the mobile screens (one source, no drift). ──

export interface CreatedShare {
  reminder_share_id: string;
  token: string;
  item_count: number;
}
export interface ShareRow {
  reminder_share_id: string;
  title: string | null;
  item_count: number;
  accept_count: number;
  revoked_at: unknown;
}
export interface PreviewReminder {
  type: string;
  date: string | null; // "M/D"
  label: string | null;
}
export interface PreviewItem {
  name: string | null;
  reminders: PreviewReminder[];
}
export interface SharePreview {
  title: string | null;
  item_count: number;
  items: PreviewItem[];
}
export interface AcceptResult {
  contactsCreated: number;
  remindersCreated: number;
  skipped: number;
}

/**
 * "today" / "tomorrow" / "in 3 days" — the one phrasing the reminder push and the in-app
 * banner it opens both use, so they can't drift (legacy `bday.py` PushMessage.days_until).
 * Negative input clamps to "today": a push dispatched late is still about today. A caller
 * that wants to HIDE a past date guards before calling.
 */
export function daysAwayLabel(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}
